import type { RoundResult, UserProfile } from '../types';

export const METRONOME_SURVIVAL_OFF_BEAT_LIMIT = 3;
export const METRONOME_SURVIVAL_APPEND_THRESHOLD = 20;
/** Max ms from nearest metronome tick to count as on-beat. */
export const METRONOME_RHYTHM_TOLERANCE_MS = 120;

/** Distance in ms from the nearest beat (last tick or next tick). */
export function rhythmDistanceFromBeat(
  nowMs: number,
  lastTickMs: number,
  beatMs: number,
): number {
  const sinceLast = nowMs - lastTickMs;
  if (sinceLast <= 0) return Math.abs(sinceLast);
  return Math.min(sinceLast, beatMs - sinceLast);
}

export function isOnBeat(
  nowMs: number,
  lastTickMs: number,
  beatMs: number,
  toleranceMs = METRONOME_RHYTHM_TOLERANCE_MS,
): boolean {
  if (lastTickMs <= 0) return false;
  return rhythmDistanceFromBeat(nowMs, lastTickMs, beatMs) <= toleranceMs;
}

export function displaySurvivalChar(ch: string): string {
  if (ch === ' ') return 'mellemrum';
  if (ch === '\n') return 'linjeskift';
  return ch;
}

export function formatSurvivalEndDetail(round: RoundResult): string {
  if (round.survivalEndReason === 'mistake') {
    const exp = round.survivalMistakeExpected;
    const typed = round.survivalMistakeTyped;
    if (exp != null && typed != null) {
      const pos =
        round.survivalMistakeAt != null
          ? ` (tegn ${round.survivalMistakeAt + 1})`
          : '';
      return `Slut: du skrev «${displaySurvivalChar(typed)}», skulle være «${displaySurvivalChar(exp)}»${pos}.`;
    }
    return 'Slut: fejl i teksten.';
  }
  if (round.survivalEndReason === 'rhythm') {
    const streak =
      round.survivalOffBeatStreak ?? METRONOME_SURVIVAL_OFF_BEAT_LIMIT;
    if (round.survivalRhythmKind === 'missed') {
      return `Slut: ${streak} tiks i træk uden tast.`;
    }
    const ms = round.survivalLastOffBeatMs;
    const msPart =
      ms != null ? ` — ${Math.round(ms)} ms fra nærmeste tik` : '';
    return `Slut: ${streak} tastetryk i træk ude af takt${msPart}.`;
  }
  return 'Slut: du stoppede selv.';
}

export function buildMetronomeWordPool(seedText: string): string[] {
  const tokens = seedText.split(/\s+/).filter((w) => w.length > 0);
  return tokens.length > 0 ? tokens : ['af', 'og', 'er', 'til'];
}

export function appendMetronomeSurvivalSnippet(
  pool: string[],
  needsLeadingSpace: boolean,
): string {
  const count = 4 + Math.floor(Math.random() * 4);
  const words: string[] = [];
  for (let i = 0; i < count; i++) {
    words.push(pool[Math.floor(Math.random() * pool.length)]!);
  }
  return `${needsLeadingSpace ? ' ' : ''}${words.join(' ')}`;
}

export function formatSurvivalMs(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

export function metronomeSurvivalRecordMs(user: UserProfile): number {
  return user.metronomeSurvivalRecord?.bestMs ?? 0;
}

export function updateMetronomeSurvivalRecord(
  user: UserProfile,
  survivalMs: number,
): { user: UserProfile; isNewRecord: boolean } {
  const prev = user.metronomeSurvivalRecord;
  const isNewRecord = !prev || survivalMs > prev.bestMs;
  const now = new Date().toISOString();
  return {
    user: {
      ...user,
      metronomeSurvivalRecord: {
        bestMs: isNewRecord ? survivalMs : prev!.bestMs,
        attempts: (prev?.attempts ?? 0) + 1,
        lastPlayedAt: now,
      },
    },
    isNewRecord,
  };
}
